"use client";

import * as React from "react";
import { format } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLocalization } from "@/context/localization-context";
import { useIsMobile } from "@/hooks/use-mobile";

type DatePickerWithRangeProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> & {
  value?: DateRange | undefined;
  onChange?: (range: DateRange | undefined) => void;
  variant?: "default" | "ghost";
};

export function DatePickerWithRange({
  className,
  onChange,
  value,
  variant = "default",
  ...props
}: DatePickerWithRangeProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(value);
  const { locale, t } = useLocalization();
  // two months side by side do not fit a phone
  const narrow = useIsMobile(640);
  const dateLocale = locale === "cs" ? cs : enUS;
  const dateFormat = locale === "cs" ? "d. MMM y" : "LLL dd, y";

  React.useEffect(() => {
    setDate(value);
  }, [value]);

  const handleSelect = (range: DateRange | undefined) => {
    setDate(range);
    if (onChange) {
      onChange(range);
    }
  };

  // Check if className contains width overrides (w-full, w-auto, w-fit, or specific widths like w-[300px])
  const hasWidthOverride = className ? /\b(w-full|w-auto|w-fit|w-\[[\d\w]+\]|w-\d+)\b/.test(className) : false;
  
  return (
    <div className={cn("grid gap-2", className)} {...props}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={variant === "ghost" ? "ghost" : "outline"}
            className={cn(
              variant === "ghost"
                ? "justify-start text-left font-normal text-sm text-muted-foreground hover:text-foreground"
                : "justify-start text-left font-normal",
              !date && "text-muted-foreground",
              // Only apply default width if no width override is provided to container
              !hasWidthOverride && (variant === "ghost" ? "w-[280px]" : "w-[300px]"),
              // If width override is provided to container, make button responsive
              hasWidthOverride && "w-full min-w-0"
            )}
          >
            <CalendarIcon
              className={variant === "ghost" ? "mr-2 h-4 w-4" : ""}
            />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, dateFormat, { locale: dateLocale })} -{" "}
                  {format(date.to, dateFormat, { locale: dateLocale })}
                </>
              ) : (
                format(date.from, dateFormat, { locale: dateLocale })
              )
            ) : (
              <span>{t("date.pickDate")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={narrow ? 1 : 2}
            locale={dateLocale}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
